/**
 * Non-blocking queue of backend events for the game loop (port addition). Server events and connection status
 * changes arrive asynchronously; FWP._main takes them one frame at a time in the states where it can act on them
 * (a profile reload only when the interface is idle, the connection indicator always), so nothing runs inside a
 * network callback and the blocking NET_INIT bridge is not involved.
 */

export type NetEvent =
  /**
   * Another connection of this player changed the stored profile (the game reloads it when idle). After missed
   * events that cannot be replayed (an event gap) the queue holds one with `revision: Infinity`: reload in any case.
   */
  | { type: 'profile.changed'; revision: number }
  | { type: 'status'; status: 'open' | 'reconnecting' | 'closed' };

export class NetEventQueue {
  private items: NetEvent[] = [];
  private offs: (() => void)[] = [];

  /** Subscribes to a GameBackend (services without events, e.g. test doubles, simply produce none). */
  attach(services: any): void {
    this.detach();
    if (!services) return;
    if (typeof services.on === 'function') {
      this.offs.push(services.on('profile.changed', (p: any) => this.push({ type: 'profile.changed', revision: Number(p && p.revision) || 0 })));
    }
    if (typeof services.onConnectionStatus === 'function') {
      this.offs.push(services.onConnectionStatus((status: any) => this.push({ type: 'status', status })));
    }
    if (typeof services.onEventGap === 'function') {
      // Missed events cannot be replayed: the stored profile may have changed meanwhile, reload it when idle.
      this.offs.push(services.onEventGap(() => this.push({ type: 'profile.changed', revision: Number.POSITIVE_INFINITY })));
    }
  }

  detach(): void {
    for (const off of this.offs) {
      try {
        off();
      } catch {
        /* ignore */
      }
    }
    this.offs = [];
  }

  push(e: NetEvent): void {
    // Only the newest profile change matters: one reload shows it.
    if (e.type === 'profile.changed') {
      const prev = this.items.find((i) => i.type === 'profile.changed') as { revision: number } | undefined;
      if (prev) {
        prev.revision = Math.max(prev.revision, e.revision);
        return;
      }
    }
    this.items.push(e);
  }

  /** Removes and returns the first queued event of `type`. */
  take<T extends NetEvent['type']>(type: T): Extract<NetEvent, { type: T }> | undefined {
    const i = this.items.findIndex((e) => e.type === type);
    return i < 0 ? undefined : (this.items.splice(i, 1)[0] as Extract<NetEvent, { type: T }>);
  }

  get size(): number {
    return this.items.length;
  }
}
